import React, { useEffect, useState } from "react";
import { Container, Row, Col, Button, Form } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useHistory } from "react-router-dom";
import Select from "react-select";

import { REACT_APP_SERVER_WAREHOUSE_SERVER_BASE_URL } from "config";

const GroupForm = ({ initialValues }) => {
  let history = useHistory();

  const [availableChildGroups, setAvailableChildGroups] = useState([]);
  const [selectedChildGroups, setSelectedChildGroups] = useState([]);
  const [brands, setBrands] = useState([]);
  const [isAnUpdate, setIsAnUpdate] = useState(
    initialValues?._id ? true : false
  );

  const submit = async (values, { resetForm }) => {
    try {
      let response;
      if (isAnUpdate) {
        response = await axios.put(
          `${REACT_APP_SERVER_WAREHOUSE_SERVER_BASE_URL}/api/admin/groups/${initialValues._id}`,
          values
        );
      } else {
        response = await axios.post(
          `${REACT_APP_SERVER_WAREHOUSE_SERVER_BASE_URL}/api/admin/groups`,
          values
        );
      }
      if (response.status === 200) {
        resetForm();
        history.push("/admin/groups");
      }
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const GroupSchema = Yup.object().shape({
    slug: Yup.string()
      .matches(
        /^[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*$/,
        "Slug must be in kebab-case (e.g. 'my-slug')"
      )
      .min(1, "Too Short!")
      .required("Required"),
    name: Yup.string().min(1, "Too Short!").required("Required"),
    forCategory: Yup.string().min(1, "Too Short!").required("Required"),
    forBrandBCId: Yup.string().nullable(),
    childGroups: Yup.array(),
    level: Yup.number().required("Required"),
  });

  const formik = useFormik({
    initialValues: initialValues
      ? initialValues
      : {
          slug: null,
          name: null,
          forCategory: "servers",
          forBrandBCId: null,
          childGroups: [],
          level: 3,
        },
    validateOnMount: true,
    validationSchema: GroupSchema,
    onSubmit: submit,
  });

  const { slug, name, forCategory, forBrandBCId, childGroups, level } =
    formik.values;

  useEffect(() => {
    getAndSetAvailableChildGroups();
    getAndSetBrands();
  }, [level, forCategory, isAnUpdate]);

  useEffect(() => {
    formik.setFieldValue(
      "childGroups",
      selectedChildGroups.map((group) => group._id)
    );
  }, [selectedChildGroups]);

  useEffect(() => {
    setIsAnUpdate(initialValues?._id ? true : false);
  }, [initialValues]);

  // Update form when brands are loaded for updates
  useEffect(() => {
    if (isAnUpdate && initialValues?.forBrandBCId && brands.length > 0) {
      const brand = brands.find(
        (b) => b.id.toString() === initialValues.forBrandBCId.toString()
      );
      if (brand) {
        formik.setFieldValue("forBrandBCId", brand.id.toString());
      }
    }
  }, [brands, isAnUpdate, initialValues]);

  const getAndSetBrands = async () => {
    try {
      const response = await axios.get(
        `${REACT_APP_SERVER_WAREHOUSE_SERVER_BASE_URL}/api/admin/bc/brands`
      );
      if (response.status === 200) {
        setBrands(response.data?.brands || []);
      }
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const getAndSetAvailableChildGroups = async () => {
    try {
      const response = await axios.get(
        `${REACT_APP_SERVER_WAREHOUSE_SERVER_BASE_URL}/api/admin/groups?forCategory=${forCategory}&level=${
          parseInt(level) + 1
        }`
      );
      if (response.status === 200) {
        setAvailableChildGroups(response.data?.groups || []);
        if (isAnUpdate) {
          setSelectedChildGroups(
            response.data?.groups.filter((group) =>
              childGroups.includes(group._id)
            ) || []
          );
        }
      }
    } catch (error) {
      console.log("error: ", error);
    }
  };

  return (
    <div>
      <Container>
        <Row>
          <Col xs={{ span: 10, offset: 1 }} md={{ span: 6, offset: 3 }}>
            <div className="page">
              <Form onSubmit={formik.handleSubmit}>
                <Form.Group controlId="fullName" size="lg">
                  <Form.Label>slug</Form.Label>
                  <Form.Control
                    type="text"
                    name="slug"
                    value={slug}
                    onChange={formik.handleChange}
                    isValid={formik.touched.slug && !formik.errors.slug}
                  />
                </Form.Group>
                <Form.Group controlId="name" size="lg">
                  <Form.Label>name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={name}
                    onChange={formik.handleChange}
                    isValid={formik.touched.name && !formik.errors.name}
                  />
                </Form.Group>

                <Form.Group controlId="forCategory" size="lg">
                  <Form.Label>For Category</Form.Label>
                  <Form.Control
                    as="select"
                    name="forCategory"
                    value={forCategory}
                    onChange={(e) => {
                      console.log("e.target.value", e.target.value);
                      formik.setFieldValue("forCategory", e.target.value);
                    }}
                  >
                    <option value="servers">Servers</option>
                    <option value="storage">Storage</option>
                    <option value="switches">Switches</option>
                  </Form.Control>
                </Form.Group>

                <Form.Group controlId="forBrandBCId" size="lg">
                  <Form.Label>For Brand (Optional)</Form.Label>
                  <Select
                    options={brands.map((brand) => ({
                      value: brand.id.toString(),
                      label: brand.name,
                    }))}
                    isClearable={true}
                    isSearchable={true}
                    placeholder="Select a brand (optional)"
                    onChange={(option) => {
                      formik.setFieldValue(
                        "forBrandBCId",
                        option ? option.value.toString() : null
                      );
                    }}
                    value={
                      forBrandBCId && brands.length > 0
                        ? brands
                            .filter(
                              (brand) =>
                                brand.id.toString() === forBrandBCId.toString()
                            )
                            .map((brand) => ({
                              value: brand.id.toString(),
                              label: brand.name,
                            }))[0] || null
                        : null
                    }
                    name="forBrandBCId"
                  />
                </Form.Group>

                <Form.Group controlId="level" size="lg">
                  <Form.Label>Level</Form.Label>
                  <Form.Control
                    as="select"
                    name="level"
                    value={level}
                    onChange={(e) => {
                      console.log("e.target.value", e.target.value);
                      formik.setFieldValue("level", e.target.value);
                    }}
                  >
                    <option value={1}>1 (Main Group)</option>
                    <option value={2}>2 (Sub Group) </option>
                    <option value={3}>3 (Product Group)</option>
                  </Form.Control>
                </Form.Group>
                <Form.Group controlId="childGroups" size="lg">
                  <Form.Label>
                    Child Groups (Level {parseInt(level) + 1})
                  </Form.Label>
                  <Select
                    options={availableChildGroups.map((group) => ({
                      value: group._id,
                      label: group.name,
                    }))}
                    isMulti={true}
                    closeMenuOnSelect={false}
                    onChange={(e) => {
                      setSelectedChildGroups(
                        e.map((group) => {
                          return { name: group.label, _id: group.value };
                        })
                      );
                    }}
                    value={selectedChildGroups.map((group) => ({
                      value: group._id,
                      label: group.name,
                    }))}
                    name="childGroups"
                  />
                </Form.Group>
                <Button
                  block
                  size="lg"
                  type="submit"
                  disabled={Object.keys(formik.errors).length !== 0}
                >
                  {isAnUpdate ? "Update" : "Create New"}&nbsp;Group
                </Button>
                {Object.keys(formik.errors).map((key) => {
                  return (
                    <p>
                      {key}: {formik.errors[key]}
                    </p>
                  );
                })}
                {childGroups.map((childGroup) => {
                  return <div key={childGroup}>group: {childGroup}</div>;
                })}
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default GroupForm;
